/**
 * Sekmeler arası klavyeyle geçiş (#476). Ctrl+Tab / Ctrl+PgUp-PgDn / Ctrl+1…9 tarayıcıya ait,
 * sayfa yakalayamıyor; bu yüzden Alt'lı kısayollar kullanılıyor.
 */
import type { TabModel } from '../model/types';

export type TabCommand =
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'nth'; index: number }
  | { type: 'home' }
  | { type: 'close' }
  | { type: 'switcher'; back: boolean };

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function isEditable(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
}

/**
 * Tuşu sekme komutuna çevirir; komut değilse null.
 * - Ctrl+Alt (AltGr: Türkçe Windows'ta @, € …) hiç sayılmaz.
 * - Mac'te Option+harf metin kutusunda karakter üretir (Türkçe Mac'te Option+Q = @): orada
 *   Option'lı harf/rakam kısayolları devre dışı; Ctrl+Option ile çalışır.
 */
export function tabCommandOf(
  e: Pick<KeyboardEvent, 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'code' | 'key' | 'target'>,
  standalone = false,
): TabCommand | null {
  // Kurulu uygulama (PWA) penceresinde Ctrl+Tab sayfaya ulaşabiliyor: SSMS'teki gibi değiştirici.
  if (standalone && e.ctrlKey && !e.altKey && e.key === 'Tab') return { type: 'switcher', back: e.shiftKey };
  const mac = isMac();
  // Mac'te AltGr yok: metin kutusundayken de çalışsın diye Ctrl+Option da kabul edilir.
  const macCtrlAlt = mac && e.ctrlKey && e.altKey;
  if (!e.altKey || e.metaKey || (e.ctrlKey && !macCtrlAlt)) return null;
  if (e.code === 'PageDown') return { type: 'next' };
  if (e.code === 'PageUp') return { type: 'prev' };
  // Mac'te Option+harf/rakam metin kutusunda karakter üretir: orada yalnız sayfa kısayolları.
  if (mac && !macCtrlAlt && isEditable(e.target)) return null;
  const digit = /^Digit([0-9])$/.exec(e.code);
  if (digit) return digit[1] === '0' ? { type: 'home' } : { type: 'nth', index: Number(digit[1]) - 1 };
  if (e.code === 'KeyW') return { type: 'close' };
  if (e.code === 'KeyQ') return { type: 'switcher', back: e.shiftKey };
  return null;
}

/** Son kullanılan sırası (MRU): en son etkin olan başta. */
export function mruOrder(tabs: TabModel[]): TabModel[] {
  return [...tabs].sort((a, b) => b.lastActiveAt - a.lastActiveAt);
}

/** Görünen sırada bir sonraki / önceki sekme (ana sayfa = null, döngüsel). */
export function stepTab(tabs: TabModel[], activeId: string | null, dir: 1 | -1): string | null {
  const ids: Array<string | null> = [null, ...tabs.map((t) => t.tabId)];
  const i = Math.max(0, ids.indexOf(activeId));
  return ids[(i + dir + ids.length) % ids.length] ?? null;
}
